<script setup lang="ts">
import { computed, ref, inject, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const detailBlacklist = ref(dialogRef?.value.options.data.detailBlacklist);
const closeModal = dialogRef?.value.options.emits.closeModal;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const configurationModule = useItemConfigurationStore();
const isLoading = ref(false);
const isShowError = ref(false);
const isEmailDisabled = computed(() => !!detailBlacklist.value.phone);
const isPhoneDisabled = computed(() => !!detailBlacklist.value.email);
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const atLeastOneRequired = helpers.withMessage(
  t('validate.required'),
  helpers.withAsync(async () => {
    const hasEmail = !!detailBlacklist.value.email;
    const hasPhone = !!detailBlacklist.value.phone;
    return hasEmail !== hasPhone;
  })
);

const validEmail = helpers.withMessage(
  t('voucher.tableHeader.email_invalid'),
  helpers.withParams({ type: 'validEmail' }, (value: any) => {
    if (!value) {
      return true;
    }
    return emailRegex.test(value);
  })
);

const rules = {
  email: {
    validEmail
  },
  phone: {},
  atLeastOneRequired
};

const $v = ref();
$v.value = useVuelidate(rules, detailBlacklist.value);

const submitAgencyBlacklist = () => {
  $v.value.value.$validate();
  isShowError.value = true;
  if (!$v.value.value.$error) {
    isLoading.value = true;
    configurationModule
      .addListAgency(detailBlacklist.value)
      .then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        closeModal();
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
      })
      .finally(() => {
        isLoading.value = false;
        isShowError.value = false;
      });
  }
};
</script>

<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="agency-blacklist-modal flex-col">
    <div class="agency-title mb-2">{{ t('gqt_admin_setup.agency.please_enter_agency') }}</div>
    <div class="agency-input-field">
      <div class="phone-input col-6">
        <div class="mb-1">{{ t('gqt_admin_setup.agency.agency_phone') }}</div>
        <InputText id="agency-phone" :disabled="isPhoneDisabled" type="text" v-model="detailBlacklist.phone" :maxlength="100" class="w-full" :class="isShowError && $v.value.atLeastOneRequired.$invalid ? 'p-invalid' : ''"></InputText>
        <p class="text-red-500 pt-1" v-if="isShowError && $v.value.atLeastOneRequired.$invalid">{{ $t('validate.required') }}</p>
      </div>
      <div class="email-input col-6">
        <div class="mb-1">{{ t('gqt_admin_setup.agency.agency_email') }}</div>
        <InputText
          id="agency-email"
          :disabled="isEmailDisabled"
          type="text"
          v-model="detailBlacklist.email"
          :maxlength="100"
          class="w-full"
          :class="isShowError && ($v.value.email.$error || $v.value.atLeastOneRequired.$invalid) ? 'p-invalid' : ''"
        ></InputText>
        <p class="text-red-500 pt-1" v-if="isShowError && $v.value.email.$error">{{ $v.value.email.$errors[0]?.$message }}</p>
        <p class="text-red-500 pt-1" v-else-if="isShowError && $v.value.atLeastOneRequired.$invalid">{{ $t('validate.required') }}</p>
      </div>
    </div>
    <div class="btn-center mt-2 w-full flex justify-content-center">
      <Button :label="$t('common.buttons.submit')" @click="submitAgencyBlacklist()" />
    </div>
  </div>
</template>
